/**
 * Original-document fetch service client.
 * Wraps /api/knowledge-bases/{kb}/documents/{id}/fetch-original* endpoints.
 * Jobs run server-side; poll getForDocument until a terminal status, then the
 * standard /pdf endpoint serves the stored file.
 */

import apiClient from './api';

export type OriginalFetchStatus =
  | 'pending'
  | 'running'
  | 'success'
  | 'no_pdf'
  | 'failed'
  | 'cancelling'
  | 'cancelled';

export const ACTIVE_FETCH_STATUSES: OriginalFetchStatus[] = ['pending', 'running', 'cancelling'];

export interface OriginalFetchProgressInfo {
  done: number;
  total: number | null;
  unit: 'pages' | 'sources';
}

export interface OriginalFetchJob {
  id: string;
  kb_id: string;
  document_id: string;
  document_type: 'patent' | 'scientific_publication';
  user_id: string;
  status: OriginalFetchStatus;
  progress: OriginalFetchProgressInfo;
  result?: { source_url?: string | null };
  error?: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export type StartFetchResponse =
  | { status: 'already_stored' }
  | { status: 'job'; job: OriginalFetchJob };

export const documentFetchService = {
  /** Start (or attach to the already-running) fetch job for a document. */
  async start(kbId: string, documentId: string): Promise<StartFetchResponse> {
    const response = await apiClient.post<StartFetchResponse>(
      `/knowledge-bases/${kbId}/documents/${documentId}/fetch-original`
    );
    return response.data;
  },

  /** Poll the latest fetch job for a document. */
  async getForDocument(kbId: string, documentId: string): Promise<OriginalFetchJob> {
    const response = await apiClient.get<OriginalFetchJob>(
      `/knowledge-bases/${kbId}/documents/${documentId}/fetch-original/job`
    );
    return response.data;
  },

  /** All active fetch jobs for a KB (page-load resume). */
  async listActive(kbId: string): Promise<OriginalFetchJob[]> {
    const response = await apiClient.get<{ jobs: OriginalFetchJob[] }>(
      `/knowledge-bases/${kbId}/fetch-original/active`
    );
    return response.data.jobs;
  },

  /** Request cooperative cancellation of the document's active fetch job. */
  async cancel(kbId: string, documentId: string): Promise<void> {
    await apiClient.post(`/knowledge-bases/${kbId}/documents/${documentId}/fetch-original/cancel`);
  },
};
